<template>
  <validation-observer
    v-if="shouldQuestionRender && isRenderAfterFullfilled"
    v-slot="observer"
    slim
  >
    <form-section
      :id="questionId || 'driver-cbt'"
      :validated="formObserver.validated"
      v-bind="observer"
    >
      <form-row
        :label="computedTitle"
        :help-text="helpText ? helpText.value || helpText : 'Most learner motorcycle and moped riders must have completed a Compulsory Basic Training (CBT) course before riding a moped or motorcycle on the road.'"
        :error="formObserver.validated && observer.failed"
      >
        <validation-provider
          v-slot="{ failed }"
          :vid="`${stepId}_driver-cbt`"
          name="Passed CBT"
          rules="required"
        >
          <radio
            v-model="questionData.driverCbt"
            :name="`${stepId}-driver-cbt`"
            :items="constants.YES_NO"
            :error="failed"
          />
        </validation-provider>
      </form-row>
      <!-- CBT PASS DATE -->
      <form-row
        v-if="questionData.driverCbt"
        question="If yes, on what date? (mm/yyyy)"
        help-text="Please state the month and year you passed your CBT."
        :error="formObserver.validated && !observer.valid"
        gap="48"
      >
        <time-zone-warning
          v-if="isWarningTextShown"
          :question-date="questionData.cbtDate"
        />
        <div class="dates">
          <div>
            <validation-provider
              v-slot="{ failed }"
              :vid="`${stepId}_date-of-cbt_month`"
              name="Month of CBT completion"
              :rules="{ listOptionRequired: questionData.driverCbt }"
            >
              <qz-select
                v-model="dateOfCbt.month"
                :data-test="`${snakeCase(questionFields.cbtDate)}_month`"
                :items="monthsOptions"
                placeholder="Select month"
                :error="failed || isDateOfCbtFailed"
              />
            </validation-provider>
          </div>
          <div>
            <validation-provider
              v-slot="{ failed }"
              :vid="`${stepId}_date-of-cbt_year`"
              name="Year of CBT completion"
              :rules="{ listOptionRequired: questionData.driverCbt }"
            >
              <qz-select
                v-model="dateOfCbt.year"
                :data-test="`${snakeCase(questionFields.cbtDate)}_year`"
                :items="yearsToDob()"
                placeholder="Select year"
                :error="failed || isDateOfCbtFailed"
              />
            </validation-provider>
          </div>
        </div>
        <validation-provider
          v-slot="{ errors, failed }"
          ref="providerCbtDate"
          :vid="`${stepId}_cbt-date`"
          name="Date of driver CBT"
          :rules="cbtDateValidationRules"
          immediate
        >
          <input
            type="hidden"
            :value="questionData.cbtDate"
          >
          <div
            v-if="failed && isDateOfCbtErrorShown"
            class="error--text mt-4 fz12"
          >
            {{ errors[0] }}
          </div>
        </validation-provider>
      </form-row>
    </form-section>
  </validation-observer>
</template>

<script>
import {
  ExtDate,
  UtcDate,
  checkDateObjFilled,
  getDayLessDateTemplate,
  getRangeOfYears,
} from '@/helpers/date';
import {
  monthsOptions,
} from '@/helpers/common';

import { computed } from '@vue/composition-api';
import useTimeZoneWarning from '@/composables/useTimeZoneWarning.js';

import QuestionBase from './Base';
import TimeZoneWarning from '@/components/TimeZoneWarning/TimeZoneWarning.vue';

export default {
  name: 'question-driver-cbt',
  extends: QuestionBase,
  components: {
    TimeZoneWarning,
  },
  data () {
    return {
      fields: ['driverCbt', 'cbtDate'],
      dateOfCbt: getDayLessDateTemplate(),
    };
  },
  computed: {
    minCbtDate () {
      const dateOfBirth = new UtcDate(this.stepData.dateOfBirth);
      if (dateOfBirth.isValid()) return dateOfBirth;
      return null;
    },

    yearOptions () {
      if (!this.minCbtDate) return [];
      const start = this.minCbtDate.getFullYear();
      const end = new UtcDate().getFullYear();
      return Array(end - start + 1).fill().map((_, idx) => start + idx).reverse();
    },

    computedTitle () {
      return this.isAdditional
        ? this.titleAdditional || 'Has this rider passed their CBT?'
        : this.title || 'Have you passed your CBT?';
    },

    isDateOfCbtErrorShown () {
      return this.formObserver.validated ||
          (this.questionData.cbtDate &&
            new ExtDate(this.questionData.cbtDate).isValid());
    },
    isDateOfCbtFailed () {
      const observerField = this.getFieldFromFormObserver('cbt-date');
      return observerField?.failed && this.isDateOfCbtErrorShown;
    },
  },
  watch: {
    'stepData.typeOfDriving' (val) {
      if (val === 'P') this.questionData.driverCbt = true;
    },

    'questionData.driverCbt' (val) {
      if (!val) {
        this.dateOfCbt.month = null;
        this.dateOfCbt.year = null;
        this.questionData.cbtDate = null;
      }
    },

    dateOfCbt: {
      handler (newDate) {
        if (checkDateObjFilled(newDate, ['month', 'year'])) {
          this.questionData.cbtDate = null;
          return;
        }
        const date = new ExtDate(newDate.year, newDate.month - 1, 1);
        this.questionData.cbtDate = date.isValid() ? date.format() : null;
      },
      deep: true,
    },

    'stepData.cbtDate': {
      // update local date of cbt model on stepData change
      handler (stepCbtDate) {
        if (stepCbtDate) {
          const date = new ExtDate(stepCbtDate);
          this.dateOfCbt.month = date.getMonth() + 1;
          this.dateOfCbt.year = date.getFullYear();
        }
      },
      immediate: true,
    },
  },
  methods: {
    yearsToDob () {
      const currentYear = new Date().getFullYear();
      const dobYear = this?.stepData?.dateOfBirth.slice(0, 4);
      const difference = currentYear - dobYear;
      return !difference ? getRangeOfYears(5) : getRangeOfYears(difference - 1);
    },
    getFieldFromFormObserver (fieldName) {
      return this.formObserver.fields[`${this.stepId}_${fieldName}`];
    },
  },
  setup (props) {
    const cbtDateValidationRules = computed(() => {
      const defaultRules = {
        cbtDate: props.stepData.cbtDate,
        checkFutureCbtDate: props.stepData.cbtDate,
      };

      return props.validationRules ? { ...props.validationRules.field, ...defaultRules } : defaultRules;
    });

    const isWarningTextShown = computed(() => {
      return useTimeZoneWarning(
        props.formObserver,
        `${props.stepId}_cbt-date`,
        cbtDateValidationRules.value,
      );
    });

    return {
      monthsOptions,
      cbtDateValidationRules,
      isWarningTextShown,
    };
  },
};
</script>

<style lang="scss" scoped>
  .dates {
    display: flex;
    justify-content: space-between;

    & > * {
      width: 100%;
      flex: 1 0 0;
      margin: 0 10px;
      &:first-child { margin-left: 0; }
      &:last-child { margin-right: 0; }
    }
  }
</style>
